import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { firstValueFrom } from 'rxjs';
import {
  CementoAccessPoint,
  CementoMapaAddress,
} from 'src/app/models/cemento-checkout';
import { ModalAlertComponent } from 'src/app/shared/modal-alert/modal-alert.component';
import { ResponsiveLayoutService } from 'src/app/services/responsive-layout.service';
import { CementoCoverageService } from 'src/app/services/cemento-coverage.service';
import { CementoCheckoutFlowService } from 'src/app/services/cemento-checkout-flow.service';
import { StepService } from 'src/app/services/step.service';
import { getCementoRegionCode } from 'src/app/shared/utils/cemento-region-code';
import { REGIONES_COMUNA } from 'src/app/shared/utils/regiones';

type DispatchAddressField = 'region' | 'comuna' | 'street' | 'number';

const DISPATCH_FIELD_LABELS: Record<DispatchAddressField, string> = {
  region: 'Región',
  comuna: 'Comuna',
  street: 'Calle',
  number: 'Número',
};


@Component({
  selector: 'app-cemento-mapa',
  templateUrl: './cemento-mapa.component.html',
  styleUrls: ['./cemento-mapa.component.css'],
})
export class CementoMapaComponent implements OnInit {
  lat = -33.452;
  lng = -70.643007;
  center: google.maps.LatLngLiteral = { lat: -33.452, lng: -70.643007 };
  zoom = 15;
  geocoder = new google.maps.Geocoder();

  readonly regionesComunas = REGIONES_COMUNA;

  readonly mapOptions = {
    scrollwheel: true,
    gestureHandling: 'auto',
  };

  readonly form: FormGroup;

  blueMarkerVisible = false;
  accessPoint: CementoAccessPoint | null = null;
  /** Misma semántica que mapa: true si el último resultado de Google incluye número de calle. */
  optional = false;
  isCheckingCoverage = false;
  dispatchValidationAttempted = false;

  constructor(
    private readonly fb: FormBuilder,
    private readonly router: Router,
    private readonly modalService: NgbModal,
    private readonly spinner: NgxSpinnerService,
    private readonly stepService: StepService,
    public readonly responsiveService: ResponsiveLayoutService,
    private readonly coverageService: CementoCoverageService,
    private readonly flow: CementoCheckoutFlowService
  ) {
    this.form = this.fb.group({
      lat: ['', [Validators.required]],
      lng: ['', [Validators.required]],
      comuna: ['', [Validators.required]],
      region: ['', [Validators.required]],
      street: ['', [Validators.required]],
      number: ['', [Validators.required]],
      address: ['', [Validators.required]],
    });
  }

  ngOnInit(): void {
    this.stepService.changeStep(1);
    const mapa = this.flow.checkoutStateValue.mapa;
    if (!mapa) {
      return;
    }

    this.form.patchValue(mapa.dispatchAddress);
    this.lat = mapa.dispatchAddress.lat;
    this.lng = mapa.dispatchAddress.lng;
    this.center = { lat: this.lat, lng: this.lng };
    this.accessPoint = mapa.accessPoint;
    this.blueMarkerVisible = true;
    this.optional =
      mapa.accessPointOptional !== undefined
        ? mapa.accessPointOptional
        : !!mapa.accessPoint?.number?.trim();
    if (this.form.valid) {
      this.form.markAsPristine();
    }
  }

  get comunaByRegion(): string[] {
    const region = this.form.get('region')?.value;
    if (!region) {
      return [];
    }

    return (
      this.regionesComunas
        .find((item) => item.nombreRegion === region)
        ?.comunas.sort((a, b) => a.localeCompare(b)) ?? []
    );
  }

  showDispatchFieldError(field: DispatchAddressField): boolean {
    if (!this.dispatchValidationAttempted) {
      return false;
    }

    const value = this.form.get(field)?.value;
    return typeof value !== 'string' || value.trim() === '';
  }

  /** Igual que ``mapa``: despacho validado (formulario válido y sin cambios desde la verificación). */
  get enabledSave(): boolean {
    return this.form.valid && this.form.pristine;
  }

  get dispatchAddressLabel(): string {
    const { street, number, comuna, region } = this.form.getRawValue();
    return `${street} ${number}, ${comuna}, ${region}`;
  }

  verificarDireccion(): void {
    if (this.getMissingDispatchFields().length > 0) {
      this.dispatchValidationAttempted = true;
      return;
    }

    this.dispatchValidationAttempted = false;
    this.spinner.show();
    this.form.setErrors(null);
    const currentFormValue = this.form.getRawValue();

    this.geocoder.geocode(
      { address: this.dispatchAddressLabel },
      (results, status) => {
        if (
          status !== google.maps.GeocoderStatus.OK ||
          !results ||
          !results.length
        ) {
          this.form.setErrors({ noValid: true });
          this.spinner.hide();
          return;
        }

        const place = results[0];
        this.optional =
          place.address_components.find((item) =>
            item.types.includes('street_number')
          )?.long_name != null;
        const location = place.geometry.location;
        this.lat = location.lat();
        this.lng = location.lng();
        this.center = { lat: this.lat, lng: this.lng };
        this.zoom = 18;

        const dispatchAddress = this.buildAddressFromPlace(place, this.lat, this.lng, {
          region: currentFormValue.region,
          comuna: currentFormValue.comuna,
        });
        this.form.patchValue(dispatchAddress);

        // El pin azul parte en la misma coordenada; los textos del punto de acceso
        // se llenan solo tras reverse-geocode al arrastrar (evita copiar calle/número
        // tipeados si el geocoder no devolvió esos componentes).
        this.accessPoint = {
          lat: this.lat,
          lng: this.lng,
          address: '',
          region: '',
          comuna: '',
          street: '',
          number: '',
        };
        this.blueMarkerVisible = true;

        this.form.markAsPristine();
        this.spinner.hide();
      }
    );
  }

  async guardar(): Promise<void> {
    if (!this.form.valid || !this.accessPoint) {
      return;
    }

    if (!this.optional && !this.accessPoint.address?.trim()) {
      this.showComplementaryRequired();
      return;
    }

    const dispatchAddress = this.form.getRawValue() as CementoMapaAddress;
    const regionCode = getCementoRegionCode(dispatchAddress.region);
    if (regionCode == null) {
      this.showCoverageErrorModal();
      return;
    }
    dispatchAddress.regionCode = regionCode;

    this.flow.setMapaData({
      dispatchAddress,
      accessPoint: {
        ...this.accessPoint,
        regionCode,
      },
      accessPointOptional: this.optional,
    });

    this.isCheckingCoverage = true;
    this.spinner.show();

    try {
      const coverage = await firstValueFrom(
        this.coverageService.getCoverage(dispatchAddress.regionCode, dispatchAddress.comuna)
      );

      if (!coverage.hasCoverage) {
        this.showCoverageUnavailableModal(dispatchAddress.region, dispatchAddress.comuna);
        return;
      }

      this.router.navigateByUrl('/cemento/pago');
    } catch {
      this.showCoverageErrorModal();
    } finally {
      this.isCheckingCoverage = false;
      this.spinner.hide();
    }
  }

  onAccessPointMarkerDragEnd(event: google.maps.MapMouseEvent): void {
    if (!event.latLng) {
      return;
    }

    const lat = event.latLng.lat();
    const lng = event.latLng.lng();

    this.geocoder.geocode(
      { location: { lat, lng } },
      (results, status) => {
        if (
          status !== google.maps.GeocoderStatus.OK ||
          !results ||
          !results.length
        ) {
          return;
        }

        const place = results[0];
        this.accessPoint = this.buildAddressFromPlace(place, lat, lng, undefined, {
          omitFormFallback: true,
        });
      }
    );
  }

  private getMissingDispatchFields(): DispatchAddressField[] {
    const raw = this.form.getRawValue();
    return (Object.keys(DISPATCH_FIELD_LABELS) as DispatchAddressField[]).filter((field) => {
      const value = raw[field];
      return typeof value !== 'string' || value.trim() === '';
    });
  }

  private showComplementaryRequired(): void {
    const modalRef = this.modalService.open(ModalAlertComponent, {
      centered: true,
    });
    modalRef.componentInstance.title = 'Dirección complementaria';
    modalRef.componentInstance.description =
      'Debe ingresar la dirección complementaria.';
    modalRef.componentInstance.accion = 'Entendido';
    modalRef.componentInstance.function = () => {
      this.modalService.dismissAll();
    };
    this.blueMarkerVisible = true;
  }

  private showCoverageUnavailableModal(region: string, comuna: string): void {
    const modalRef = this.modalService.open(ModalAlertComponent, {
      centered: true,
      backdrop: 'static',
    });

    modalRef.componentInstance.title = 'Cobertura en revisión';
    modalRef.componentInstance.description =
      `Por ahora no tenemos cobertura para ${comuna}, ${region}. ` +
      'Sabemos que esto puede ser frustrante y estamos trabajando para llegar a más comunas lo antes posible.';
    modalRef.componentInstance.accion = 'Entendido';
    modalRef.componentInstance.function = () => {
      this.modalService.dismissAll();
    };
  }

  private showCoverageErrorModal(): void {
    const modalRef = this.modalService.open(ModalAlertComponent, {
      centered: true,
      backdrop: 'static',
    });

    modalRef.componentInstance.title = 'No pudimos validar tu cobertura';
    modalRef.componentInstance.description =
      'Tuvimos un problema al revisar la disponibilidad para tu dirección. ' +
      'Por favor intenta nuevamente en unos segundos.';
    modalRef.componentInstance.accion = 'Cerrar';
    modalRef.componentInstance.function = () => {
      this.modalService.dismissAll();
    };
  }

  private buildAddressFromPlace(
    place: google.maps.GeocoderResult,
    lat: number,
    lng: number,
    overrides?: Partial<Pick<CementoAccessPoint, 'region' | 'comuna' | 'street' | 'number'>>,
    options?: { omitFormFallback?: boolean }
  ): CementoAccessPoint {
    const components = place.address_components;
    const omitFormFallback = options?.omitFormFallback ?? false;
    const formRegion = (this.form.get('region')?.value as string | undefined) ?? '';
    const formComuna = (this.form.get('comuna')?.value as string | undefined) ?? '';
    const formStreet = (this.form.get('street')?.value as string | undefined) ?? '';
    const formNumber = (this.form.get('number')?.value as string | undefined) ?? '';

    return {
      lat,
      lng,
      address: place.formatted_address,
      region:
        overrides?.region ??
        components.find((item) =>
          item.types.includes('administrative_area_level_1')
        )?.long_name ??
        (omitFormFallback ? '' : formRegion),
      comuna:
        overrides?.comuna ??
        components.find((item) =>
          item.types.includes('administrative_area_level_3')
        )?.long_name ??
        (omitFormFallback ? '' : formComuna),
      street:
        overrides?.street ??
        components.find((item) => item.types.includes('route'))?.long_name ??
        (omitFormFallback ? '' : formStreet),
      number:
        overrides?.number ??
        components.find((item) => item.types.includes('street_number'))
          ?.long_name ??
        (omitFormFallback ? '' : formNumber),
    };
  }
}
